import { useEffect, useRef, useState } from 'react';
import { Box, Dialog, InputAdornment, TextField, Typography, useTheme } from '@mui/material';

import CommonButton from '../CommonButton';
import CommonModal from './CommonModal';
import { isCompleteModalType, ResGetPoolDetailsCall, ResGetStakeCall } from '@/types';
import { useWeb3 } from '@/hooks/use-web3';
import { GETPOOLDETAILS, GETSTAKE, UNSTAKETOKEN } from '@/lib/common/name-space';
import { toast } from 'react-toastify';
import { promiseAllSettled } from '@/lib/Promise';
// asset
import SymbolSvg from '@/assets/assetfi/Symbol';
import CloseSvg from '@/assets/assetfi/Close';
import CheckBoxSvg from '@/assets/assetfi/CheckBox';
import CheckBoxFilledSvg from '@/assets/assetfi/CheckBoxFilled';
import InfoSvg from '@/assets/assetfi/Info';
import { removeZeroStart } from '@/lib/util';
import { useNavigate } from 'react-router-dom';
import BigNumber from 'bignumber.js';
import Loading from '@/component/mui/Backdrop';
import { setTicker } from '@/component/balance/ticker';

interface UnstakingModalPropsType {
  handleClose: () => void;
  open: boolean;
  contractAddr: string;
  stakeIndex: string;
}

type UnstakingInfo = ResGetStakeCall & ResGetPoolDetailsCall;

const UnstakingModal = ({ handleClose, open, contractAddr, stakeIndex }: UnstakingModalPropsType) => {
  // state
  const [amount, setAmount] = useState('0');
  const [usdt, setUsdt] = useState('0');
  const [checked, setChecked] = useState(false);
  const [isCompleteModal, setIsCompleteModal] = useState<isCompleteModalType>({
    success: false,
    fail: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [stakeInfo, setStakeInfo] = useState<UnstakingInfo | null>(null);
  const address = useRef('');

  const theme = useTheme();
  const navigate = useNavigate();
  const { web3authCall } = useWeb3();

  useEffect(() => {
    (async () => {
      if (web3authCall?.connected()) {
        const addr = await web3authCall.getAddress();
        address.current = addr;
      }
    })();

    return () => {};
  }, [web3authCall]);

  useEffect(() => {
    if (stakeIndex && web3authCall?.connected()) {
      (async () => {
        try {
          const result = await promiseAllSettled([
            () => web3authCall.callStaking(GETPOOLDETAILS, contractAddr),
            () => web3authCall.callStaking(GETSTAKE, contractAddr, [address.current, stakeIndex]),
          ]);

          const obj = {
            ...result[0],
            ...result[1],
          };
          setStakeInfo(obj);
        } catch (e) {
          console.error(e);
        }
      })();
    }

    return () => {};
  }, [web3authCall, contractAddr, stakeIndex]);

  useEffect(() => {
    (async () => {
      if (open) {
        console.log(`open`);
        setUsdt(await setTicker({ navigate: navigate }));
      }
    })();

    return () => {};
  }, [open]);

  // method
  const handleConfirm = async () => {
    if (!amount) {
      toast.error('Please enter the quantity');
      return;
    }
    if (!address.current) {
      return;
    }
    if (!stakeInfo) {
      return;
    }

    if (BigNumber(amount).gt(stakeInfo.amountStaked)) {
      toast.error('Please check the unstaking amount.');
      return;
    }
    handleClose();
    setIsLoading(true);
    const code = await unStakeToken();
    setIsLoading(false);
    if (code !== 200) {
      triggerIsCompleteModal('fail');
    } else {
      triggerIsCompleteModal('success');
    }
  };
  const unStakeToken = async (): Promise<number> => {
    if (!web3authCall?.connected()) {
      return 400;
    }
    const payload = [address.current, stakeIndex, amount];
    const { code } = await web3authCall.callStaking(UNSTAKETOKEN, contractAddr, payload);
    return code;
  };
  const triggerIsCompleteModal = (key: keyof isCompleteModalType) => {
    setIsCompleteModal((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-paper': {
            width: '320px',
            padding: '24px 0px',
            borderRadius: '20px',
          },
        }}
        open={open}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'space-between',
            padding: '0px 24px',
          }}
        >
          <SymbolSvg width={24} height={24} />
          <Typography color={theme.greyscale.darkgrey2} variant="h6">
            {stakeInfo?.name}
          </Typography>

          <Box onClick={handleClose} sx={{ cursor: 'pointer' }}>
            <CloseSvg width={24} height={24} />
          </Box>
        </Box>

        <Box sx={{ marginTop: '24px', padding: '0px 16px', position: 'relative' }}>
          <Typography sx={{ marginLeft: '8px' }} variant="subtitle2" color={theme.greyscale.grey3}>
            Stake Amount
          </Typography>

          <TextField
            value={amount}
            onChange={(e) => {
              if (!e.target.value) {
                setAmount('0');
                return;
              }
              setAmount(removeZeroStart(e.target.value));
            }}
            placeholder="0"
            sx={{
              width: '100%',
              marginTop: '8px',

              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                '& input': {
                  textAlign: 'right',
                  typography: 'h4',
                  color: theme.greyscale.darkgrey2,
                  padding: '8px',
                  paddingRight: '0px',
                  paddingBottom: '28px',
                },
                '& fieldset': {
                  borderColor: theme.background.blue,
                },
                '&:hover fieldset': {
                  borderColor: theme.background.blue,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.background.blue,
                },
                '&.MuiInputBase-root': {
                  background: '#357AFF1A',
                  padding: 0,
                },
                '& .MuiTypography-root': {
                  typography: 'h5',
                  color: theme.greyscale.darkgrey2,
                },
              },
            }}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment
                  sx={{
                    fontSize: '24px',
                    paddingRight: '8px',
                    paddingBottom: '20px',
                  }}
                  position="end"
                >
                  SFL
                </InputAdornment>
              ),
            }}
          />
          <Typography sx={{ position: 'absolute', right: 24, bottom: 8 }} color={theme.greyscale.grey3} variant="body2">
            ~ {BigNumber(amount).multipliedBy(usdt).toFixed()} USD
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
            padding: '0 16px',
          }}
        >
          <InfoSvg width={14} height={14} />
          <Typography
            sx={{
              fontSize: '12px',
              lineHeight: '18px',
              fontWeight: 400,
              letterSpacing: '-0.05em',
            }}
            variant="subtitle2"
            color={theme.background.red}
          >
            The Market Price At The Time Of Unstaking Is Reflected.
          </Typography>
        </Box>

        <Box sx={{ marginTop: '24px', padding: '0px 16px' }}>
          <Typography sx={{ marginLeft: '8px' }} variant="subtitle2" color={theme.greyscale.grey3}>
            Details
          </Typography>

          <Box
            sx={{
              marginTop: '8px',
              background: theme.background.default,
              borderRadius: '10px',
              border: `0.5px solid ${theme.greyscale.grey1}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Stake Amount
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {amount}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    SFL
                  </Typography>
                </Typography>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  ~ {BigNumber(amount).multipliedBy(usdt).toFixed()}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    USD
                  </Typography>
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Possible Amount
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {stakeInfo && stakeInfo.amountStaked}
                </Typography>
              </Box>
            </Box>
            {/* <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Duration
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  1 Year
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Minimum Deposit
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  0{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    USD
                  </Typography>
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Total Rewards
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  display={'inline'}
                  sx={{
                    textDecoration: 'line-through',
                  }}
                  variant="subtitle2"
                  color={theme.greyscale.darkgrey2}
                >
                  100{' '}
                </Typography>
                <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                  USD
                </Typography>
              </Box>
            </Box> */}
          </Box>
        </Box>
        <Box
          sx={{
            padding: '24px',
            borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
            display: 'flex',
            gap: '10px',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ cursor: 'pointer' }} onClick={() => setChecked((prev) => !prev)}>
            {checked ? <CheckBoxFilledSvg width={20} height={20} /> : <CheckBoxSvg width={20} height={20} />}
          </Box>

          <Typography
            sx={{
              fontWeight: '400',
              fontSize: '13px',
              lineHeight: '15.73px',
              letterSpacing: '-0.02em',
            }}
            color={theme.greyscale.darkgrey1}
          >
            No rewards are credited for early withdrawal, and commission is required
          </Typography>
        </Box>

        <Box sx={{ marginTop: '24px', padding: '0px 16px' }}>
          {checked === true ? (
            <CommonButton
              sx={{ width: '100%', padding: '12px 20px', borderRadius: '10px' }}
              text="Confirm"
              handleClick={handleConfirm}
              variant="h6"
              color={checked ? '#ffffff' : theme.background.blue}
              bgColor={checked ? theme.background.blue : '#ffffff'}
              border={theme.background.blue}
            />
          ) : (
            <CommonButton
              disabled
              sx={{ width: '100%', padding: '12px 20px', borderRadius: '10px' }}
              text="Confirm"
              handleClick={handleConfirm}
              variant="h6"
              color={checked ? '#ffffff' : theme.background.blue}
              bgColor={checked ? theme.background.blue : '#ffffff'}
              border={theme.background.blue}
            />
          )}
          <CommonButton
            sx={{
              width: '100%',
              padding: '12px 20px',
              borderRadius: '10px',
              marginTop: '10px',
            }}
            text="Back"
            handleClick={handleClose}
            variant="h6"
            color={theme.greyscale.lightgrey2}
            border={theme.greyscale.lightgrey2}
            bgColor={'#ffffff'}
          />
        </Box>
      </Dialog>

      {/*  Confirm 성공 시 */}
      <CommonModal
        open={isCompleteModal.success}
        handleClose={() => {
          triggerIsCompleteModal('success');
        }}
        title="Unstake Complete"
        type="success"
      >
        <Typography color={theme.greyscale.grey2} variant="h6">
          Unstake Completed Successfully.
          <br />
          Thank You.
        </Typography>
      </CommonModal>

      {/*  Confirm 실패 시 */}
      <CommonModal
        open={isCompleteModal.fail}
        handleClose={() => {
          triggerIsCompleteModal('fail');
        }}
        title="Error"
        type="error"
      >
        <Typography color={theme.greyscale.grey2} variant="h6">
          Unstake Failed.
        </Typography>
      </CommonModal>

      <Loading isLoading={isLoading} />
    </>
  );
};

export default UnstakingModal;
